import React, { useRef, useState } from "react";
import { Upload, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiErrorDetail } from "../../lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml";
const MAX_MB = 5;

function resolveSrc(value) {
    if (!value) return "";
    // If it starts with /api/uploads/ we serve through BACKEND_URL
    if (value.startsWith("/api/")) {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        return `${base}${value}`;
    }
    return value;
}

/**
 * ImageUploader — replaces a plain image-URL <input> with upload + preview + paste-URL fallback.
 *
 *   <ImageUploader value={form.cover_image} onChange={(v) => setForm({...form, cover_image: v})} />
 *
 * Saves whatever URL is active (uploaded `/api/uploads/<id>` or a pasted external URL)
 * back to the parent via `onChange(url)`. Passing the empty string clears the value.
 */
export default function ImageUploader({
    value,
    onChange,
    testIdPrefix = "image-uploader",
    helperText = "JPG, PNG, WebP, GIF or SVG · max 5 MB",
}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [mode, setMode] = useState("upload"); // "upload" | "url"

    const openPicker = () => inputRef.current?.click();

    const handleFile = async (file) => {
        if (!file) return;
        if (!ACCEPT.split(",").includes(file.type)) {
            toast.error("Unsupported image type");
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File too large (max ${MAX_MB} MB)`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file) handleFile(file);
    };

    const clear = () => onChange("");

    const previewSrc = resolveSrc(value);

    return (
        <div className="space-y-3">
            {previewSrc ? (
                <div className="relative group border border-white/10 bg-[#05050e]" data-testid={`${testIdPrefix}-preview-wrapper`}>
                    <img
                        src={previewSrc}
                        alt="Preview"
                        className="w-full max-h-60 object-contain"
                        data-testid={`${testIdPrefix}-preview`}
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                    <button
                        type="button"
                        onClick={clear}
                        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 text-[11px] tracking-[0.14em] uppercase font-mono-tech bg-[#050510]/90 border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition-colors"
                        data-testid={`${testIdPrefix}-remove`}
                    >
                        <X size={12} /> Remove
                    </button>
                </div>
            ) : (
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    className="border border-dashed border-white/15 bg-[#05050e] px-6 py-10 text-center"
                    data-testid={`${testIdPrefix}-dropzone`}
                >
                    <Upload size={18} className="mx-auto text-white/40 mb-3" />
                    <div className="font-mono-tech text-[11px] tracking-[0.18em] uppercase text-white/55 mb-1">
                        Drag & drop — or
                    </div>
                    <button
                        type="button"
                        onClick={openPicker}
                        disabled={uploading}
                        className="btn-secondary !py-2 !px-4 !text-[11px]"
                        data-testid={`${testIdPrefix}-browse`}
                    >
                        {uploading ? "Uploading…" : "Choose file"}
                    </button>
                    <div className="mt-3 text-[11px] text-white/40">{helperText}</div>
                </div>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid={`${testIdPrefix}-file-input`}
            />

            <div className="flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={() => setMode(mode === "url" ? "upload" : "url")}
                    className="inline-flex items-center gap-1.5 font-mono-tech text-[10.5px] tracking-[0.18em] uppercase text-white/45 hover:text-white/85 transition-colors"
                    data-testid={`${testIdPrefix}-toggle-url`}
                >
                    <Link2 size={11} /> {mode === "url" ? "Hide URL field" : "Or paste URL"}
                </button>
                {value && mode !== "url" && (
                    <div className="font-mono-tech text-[10.5px] text-white/30 truncate max-w-[60%]" title={value}>
                        {value}
                    </div>
                )}
            </div>
            {mode === "url" && (
                <input
                    className="tech-input !text-[13px]"
                    placeholder="https://example.com/image.jpg"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`${testIdPrefix}-url-input`}
                />
            )}
        </div>
    );
}
